import type { Space, Venue } from '@Types/venue';
import type { MatrixCellState } from '../MatrixCell';

const NA: MatrixCellState = { kind: 'na' };

const required = (pass: boolean, tooltip: string): MatrixCellState =>
  pass ? { kind: 'pass', tooltip } : { kind: 'fail', tooltip };

const optional = (set: boolean, tooltip: string): MatrixCellState =>
  set
    ? { kind: 'optional-set', tooltip }
    : { kind: 'optional-missing', tooltip: tooltip || 'Not set' };

const hasText = (s: string | null | undefined): boolean =>
  typeof s === 'string' && s.trim().length > 0;

export const EB_SHARED_REQUIRED_LABELS = [
  'Venue Enabled',
  'Toast Restaurant',
  'Tripleseat Location ID',
  'Tripleseat Room ID',
] as const;

export const EB_PD_REQUIRED_LABELS = ['PD Menu', 'PD Booking Type'] as const;
export const EB_CORP_REQUIRED_LABELS = [
  'Corp Menu',
  'Corp Booking Type',
] as const;

export const EB_SHARED_OPTIONAL_LABELS = [
  'Venue Featured Asset',
  'Space Featured Asset',
  'Space Assets',
  'Floor Plan Assets',
] as const;

export const EB_PD_OPTIONAL_LABELS = [
  'PD Details',
  'PD Terms',
  'PD Policy',
] as const;

export const EB_CORP_OPTIONAL_LABELS = [
  'Corp Details',
  'Corp Terms',
  'Corp Policy',
] as const;

function sharedRequiredForVenue(venue: Venue): MatrixCellState[] {
  return [
    required(
      venue.disabledAt === null,
      venue.disabledAt === null
        ? 'Venue is enabled'
        : `Disabled at ${venue.disabledAt}`,
    ),
    required(
      venue.toastRestaurantGuid != null,
      venue.toastRestaurantGuid ?? 'Missing',
    ),
    required(
      venue.tripleSeatId != null,
      venue.tripleSeatId != null ? String(venue.tripleSeatId) : 'Missing',
    ),
  ];
}

function sharedRequiredForSpace(venue: Venue, space: Space): MatrixCellState[] {
  return [
    ...sharedRequiredForVenue(venue),
    required(
      space.tripleSeatId != null,
      space.tripleSeatId != null ? String(space.tripleSeatId) : 'Missing',
    ),
  ];
}

function pdRequiredForSpace(venue: Venue, space: Space): MatrixCellState[] {
  const hasType = (space.bookingTypes ?? []).includes('PRIVATE_DINING');
  return [
    required(
      venue.toastPrivateDiningMenuGuid != null,
      venue.toastPrivateDiningMenuGuid ?? 'Missing',
    ),
    required(hasType, hasType ? 'PRIVATE_DINING' : 'Missing'),
  ];
}

function corpRequiredForSpace(venue: Venue, space: Space): MatrixCellState[] {
  const hasType = (space.bookingTypes ?? []).includes('CORPORATE');
  return [
    required(
      venue.toastCorporateDiningMenuGuid != null,
      venue.toastCorporateDiningMenuGuid ?? 'Missing',
    ),
    required(hasType, hasType ? 'CORPORATE' : 'Missing'),
  ];
}

function sharedOptionalForSpace(venue: Venue, space: Space): MatrixCellState[] {
  const venueFeatured = venue.featuredAssetId != null;
  const spaceFeatured = space.featuredAssetId != null;
  const assetCount = space.assets?.length ?? 0;
  const floorCount = space.floorPlanAssets?.length ?? 0;
  return [
    optional(venueFeatured, venueFeatured ? 'Set' : 'Not set'),
    optional(spaceFeatured, spaceFeatured ? 'Set' : 'Not set'),
    optional(
      assetCount > 0,
      assetCount > 0 ? `${assetCount} asset(s)` : 'Not set',
    ),
    optional(
      floorCount > 0,
      floorCount > 0 ? `${floorCount} floor plan(s)` : 'Not set',
    ),
  ];
}

function pdOptionalForSpace(space: Space): MatrixCellState[] {
  return [
    optional(
      hasText(space.pdDetails),
      hasText(space.pdDetails) ? 'Set' : 'Not set',
    ),
    optional(
      hasText(space.pdTerms),
      hasText(space.pdTerms) ? 'Set' : 'Not set',
    ),
    optional(
      hasText(space.pdPolicy),
      hasText(space.pdPolicy) ? 'Set' : 'Not set',
    ),
  ];
}

function corpOptionalForSpace(space: Space): MatrixCellState[] {
  return [
    optional(
      hasText(space.corpDetails),
      hasText(space.corpDetails) ? 'Set' : 'Not set',
    ),
    optional(
      hasText(space.corpTerms),
      hasText(space.corpTerms) ? 'Set' : 'Not set',
    ),
    optional(
      hasText(space.corpPolicy),
      hasText(space.corpPolicy) ? 'Set' : 'Not set',
    ),
  ];
}

export type SpaceEvaluation = {
  eventBookingOn: boolean;
  pdOn: boolean;
  corpOn: boolean;
  sharedRequired: MatrixCellState[];
  pdRequired: MatrixCellState[];
  corpRequired: MatrixCellState[];
  sharedOptional: MatrixCellState[];
  pdOptional: MatrixCellState[];
  corpOptional: MatrixCellState[];
};

export function evaluateEventBookingForSpace(
  venue: Venue,
  space: Space,
): SpaceEvaluation {
  const sharedRequired = sharedRequiredForSpace(venue, space);
  const pdRequired = pdRequiredForSpace(venue, space);
  const corpRequired = corpRequiredForSpace(venue, space);
  const sharedAllPass = sharedRequired.every((c) => c.kind === 'pass');
  const pdOn = sharedAllPass && pdRequired.every((c) => c.kind === 'pass');
  const corpOn = sharedAllPass && corpRequired.every((c) => c.kind === 'pass');
  return {
    eventBookingOn: pdOn || corpOn,
    pdOn,
    corpOn,
    sharedRequired,
    pdRequired,
    corpRequired,
    sharedOptional: sharedOptionalForSpace(venue, space),
    pdOptional: pdOptionalForSpace(space),
    corpOptional: corpOptionalForSpace(space),
  };
}

export type VenueEvaluation = {
  sharedRequired: MatrixCellState[]; // 4 cells, last is N/A (space-level)
  pdRequired: MatrixCellState[]; // 2 cells: PD Menu (venue), PD Booking Type (N/A)
  corpRequired: MatrixCellState[];
  sharedOptional: MatrixCellState[]; // 4 cells: only venue Featured Asset evaluated
  pdOptional: MatrixCellState[]; // all N/A (space-level)
  corpOptional: MatrixCellState[]; // all N/A
};

export function evaluateEventBookingForVenue(venue: Venue): VenueEvaluation {
  const venueShared = sharedRequiredForVenue(venue);
  const venueFeatured = venue.featuredAssetId != null;
  return {
    sharedRequired: [...venueShared, NA],
    pdRequired: [
      required(
        venue.toastPrivateDiningMenuGuid != null,
        venue.toastPrivateDiningMenuGuid ?? 'Missing',
      ),
      NA,
    ],
    corpRequired: [
      required(
        venue.toastCorporateDiningMenuGuid != null,
        venue.toastCorporateDiningMenuGuid ?? 'Missing',
      ),
      NA,
    ],
    sharedOptional: [
      optional(venueFeatured, venueFeatured ? 'Set' : 'Not set'),
      NA,
      NA,
      NA,
    ],
    pdOptional: [NA, NA, NA],
    corpOptional: [NA, NA, NA],
  };
}
